import _ from 'underscore';

export const fetchResults = async ({
  liftExperimentId,
  onFailure,
  onSuccess,
  scope: {
    values: {
      aid,
      cid,
    },
  },
  fetch,
}) => {
  try {
    const res = await fetch(`/Customers(${cid})/Accounts(${aid})/GetLiftExperimentResult(${liftExperimentId})`);
    const bodyJson = await res.json();
    if (res.ok) {
      return onSuccess(bodyJson);
    }
    onFailure(res);
    return null;
  } catch (e) {
    onFailure(e);
    return null;
  }
};

const stringValues = [
  'Confidence_BSearch',
  'Confidence_Convert',
  'Confidence_Visit',
  'MeasurementCat',
];
export const transformResults = apiData => _.reduce(apiData, (transformedResults, rootArray, rootKey) => ({
  ...transformedResults,
  [rootKey]: _.map(rootArray, result => _.reduce(result, (transformedResult, value, key) => ({
    ...transformedResult,
    [key]: stringValues.includes(key) || isNaN(Number(value))
      ? value
      : Number(value),
  }), {})),
}), {});

export const getConversionData = resultData => ({
  confidence: resultData.Confidence_Convert,
  control: resultData.Control_ConverterRate,
  lift: resultData.Lift_Converter,
  treatment: resultData.Treatment_ConverterRate,
});

export const getSearchData = resultData => ({
  confidence: resultData.Confidence_BSearch,
  control: resultData.Control_BSearcherRate,
  lift: resultData.Lift_BSearcher,
  treatment: resultData.Treatment_BSearcherRate,
});

export const getVisitData = resultData => ({
  confidence: resultData.Confidence_Visit,
  control: resultData.Control_VisitorRate,
  lift: resultData.Lift_Visitor,
  treatment: resultData.Treatment_VisitorRate,
});

export const getAudienceResults = (resultsData) => {
  const audienceResults = resultsData.TargetType_Result;
  return {
    conversion: audienceResults.map((audienceData, i) => ({
      id: `${audienceData.TargetTypeId}-${i}`,
      name: audienceData.TargetTypeId,
      ...getConversionData(audienceData),
    })),
    search: audienceResults.map((audienceData, i) => ({
      id: `${audienceData.TargetTypeId}-${i}`,
      name: audienceData.TargetTypeId,
      ...getSearchData(audienceData),
    })),
    visit: audienceResults.map((audienceData, i) => ({
      id: `${audienceData.TargetTypeId}-${i}`,
      name: audienceData.TargetTypeId,
      ...getVisitData(audienceData),
    })),
  };
};

export const measurementCategories = {
  'Awareness-Search Lift': 'search',
  'Consideration-Visit Lift': 'visit',
  'Conversion-Conversion Lift': 'conversion',
};

export const getBottomPerformerResults = (resultsData) => {
  const bottomPerformerResults = resultsData.Bottom5_Result || [];
  const allData = {
    conversion: [],
    search: [],
    visit: [],
  };
  bottomPerformerResults.forEach((performerData) => {
    if (measurementCategories[performerData.MeasurementCat]) {
      const mappedData = {
        audienceId: performerData.TargetValueId,
        audienceTypeId: performerData.TargetTypeId,
        rank: performerData.Rank_Lift,
      };
      allData[measurementCategories[performerData.MeasurementCat]].push(mappedData);
    }
  });
  return allData;
};

export const getTopPerformerResults = (resultsData) => {
  const topPerformerResults = resultsData.Top5_Result;
  const allData = {
    conversion: [],
    search: [],
    visit: [],
  };
  topPerformerResults.forEach((performerData) => {
    if (measurementCategories[performerData.MeasurementCat]) {
      const mappedData = {
        audienceId: performerData.TargetValueId, // This is the ID for the value shown in the "Audience name" column.
        audienceTypeId: performerData.TargetTypeId, // This is the ID for the value shown in the "Audience type" column.
        rank: performerData.Rank_Lift,
      };
      allData[measurementCategories[performerData.MeasurementCat]].push(mappedData);
    }
  });
  // Still need to sort the data by `rank`.
  return allData;
};

export const getCampaignIds = (resultsData) => {
  let campaignIds = [];
  if (resultsData.Campaign_Result) {
    campaignIds = resultsData.Campaign_Result.map(campaignData => campaignData.CampaignId);
  }
  return campaignIds;
};

export const getCampaignResults = (resultsData, campaigns) => {
  const campaignResults = resultsData.Campaign_Result;
  return {
    conversion: campaignResults.map(campaignData => ({
      id: campaignData.CampaignId,
      name: campaigns[campaignData.CampaignId].name,
      ...getConversionData(campaignData),
    })),
    search: campaignResults.map(campaignData => ({
      id: campaignData.CampaignId,
      name: campaigns[campaignData.CampaignId].name,
      ...getSearchData(campaignData),
    })),
    visit: campaignResults.map(campaignData => ({
      id: campaignData.CampaignId,
      name: campaigns[campaignData.CampaignId].name,
      ...getVisitData(campaignData),
    })),
  };
};

export const getOverallResults = (resultsData) => {
  const overallResults = resultsData.CID_Result[0];
  return {
    conversion: getConversionData(overallResults),
    search: getSearchData(overallResults),
    visit: getVisitData(overallResults),
  };
};
